import React from "react";
import PropTypes from "prop-types";
import Header from "components/v3/header/Header";
import GoogleTracking from "services/GoogleTracking";
import EmailWithConfirm from "components/v3/email-with-confirm/EmailWithConfirm";
import BusinessweekAccountId from "components/v3/businessweek-account-id/BusinessweekAccountId";
import TermsAndPrivacyPolicyContainer from "containers/v3/TermsAndPrivacyPolicyContainer";
import PrimaryButton from "components/v3/primary-button/PrimaryButton";
import fixMobileSafariTooltips from "utils/fixMobileSafariTooltips";
import { CDS, isAppleProvider } from "common/utils/subscriptionProvider";
import { businessweekData, bbgData } from "utils/formatSubscription";
import ErrorHandler from "components/v3/error-handler/ErrorHandler";
import SubmitError from "components/v3/submit-error/SubmitError";
import { THEMES } from "common/constants/Constants";
import styles from "./styles.module.scss";

class RegisterBusinessweek extends React.Component {
    constructor(props) {
        super(props);

        this._onSubmit = this._onSubmit.bind(this);
        this._onEmailChange = this._onEmailChange.bind(this);
        this._onConfirmEmailChange = this._onConfirmEmailChange.bind(this);
        this._onAccountIdChange = this._onAccountIdChange.bind(this);
    }

    componentDidMount() {
        const { checkUser } = this.props;

        GoogleTracking.setDataLayerFields({
            action: "Businessweek Register",
            cd9: "businessweek_register",
            cd13: "register"
        });

        checkUser();

        fixMobileSafariTooltips();
    }

    _onEmailChange(event) {
        const newEmail = event.target.value.trim();

        return this.props.onEmailChange(newEmail);
    }

    _onConfirmEmailChange(event) {
        const newConfirmationEmail = event.target.value.trim();

        return this.props.onConfirmationEmailChange(newConfirmationEmail, this.props.email);
    }

    _onAccountIdChange(value) {
        const newAccountId = value.trim();

        return this.props.onAccountIdChange(newAccountId);
    }

    render() {
        const {
            errors,
            email,
            confirmationEmail,
            subscriptionId,
            provider,
            signInLink,
            location: { query = {} } = {}
        } = this.props;

        const isReferral = Boolean(provider);

        const submitDisabled = isSubmitDisabled(this.props);

        return (
            <div>
                <ErrorHandler type="register" errors={ errors }>
                    <SubmitError theme={ THEMES.ERROR_THEME_MODULE_LEVEL } />
                </ErrorHandler>
                <Header title="Link your Businessweek subscription">
                    <span className={ styles.existingAccount }>
                        Already have a Bloomberg.com account?
                        <a className={ styles.existingAccountLink } href={ signInLink }>Sign in</a>
                    </span>
                </Header>
                <div className={ styles.body }>
                    <form onSubmit={ this._onSubmit }>
                        <div className={ styles.formElement }>
                            <EmailWithConfirm
                                email={ email }
                                confirmEmail={ confirmationEmail }
                                errors={ errors }
                                handleEmailChange={ this._onEmailChange }
                                handleConfirmEmailChange={ this._onConfirmEmailChange }
                                emailIsDisabled={ isReferral && query.email }
                            />
                        </div>
                        <AccountId
                            provider={ provider }
                            subscriptionId={ subscriptionId }
                            onChange={ this._onAccountIdChange }
                            disabled={ isReferral && query.subscriptionId }
                            errors={ errors }
                        />
                        <TermsAndPrivacyPolicyContainer />
                        <div className={ styles.buttonContainer }>
                            <PrimaryButton text="Continue" disabled={ submitDisabled } />
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    _onSubmit(event) {
        event.preventDefault();

        const {
            email,
            subscriptionId,
            product,
            onBusinessweekSubmit
        } = this.props;

        const provider = this.props.provider || CDS;

        const subscription = product === "BBG"
            ? bbgData({
                subscriptionEmail: email,
                provider
            })
            : businessweekData({
                subscriptionEmail: email,
                subscriptionId,
                provider
            });

        onBusinessweekSubmit({
            email,
            subscription
        });
    }
}

function isSubmitDisabled(props) {
    const {
        email,
        confirmationEmail,
        subscriptionId,
        errors,
        provider
    } = props;

    if (isAppleProvider(provider)) {
        return !email
            || !confirmationEmail
            || errors["email"]
            || errors["confirmEmail"]
            || errors["accountId"];
    }

    return !email
        || !confirmationEmail
        || !subscriptionId
        || errors["email"]
        || errors["confirmEmail"]
        || errors["accountId"];
}

const AccountId = ({ provider, subscriptionId, onChange, disabled, errors }) => {
    if (isAppleProvider(provider)) {
        return null;
    }

    return (
        <div className={ styles.formElement }>
            <BusinessweekAccountId
                accountId={ subscriptionId }
                onChange={ onChange }
                disabled={ disabled }
                errors={ errors }
            />
        </div>
    );
};

RegisterBusinessweek.propTypes = {
    registerBusinessweek: PropTypes.func.isRequired,
    checkUser: PropTypes.func.isRequired,
    errors: PropTypes.object,
    successMessage: PropTypes.string,
    product: PropTypes.string,
    email: PropTypes.string,
    subscriptionId: PropTypes.string,
    onEmailChange: PropTypes.func,
    onAccountIdChange: PropTypes.func
};

export default RegisterBusinessweek;
